import { isObjectRecord } from '~~/helpers/dataStructure/isObjectRecord';
import { StringBoolean } from '~~/types/StringBoolean';
import type { CustomFieldsDraft, CustomFieldsType, RawCustomFields } from '~~/types/commercetools';

type CommerceToolsObject = {
  custom?: Maybe<CustomFieldsType | CustomFieldsDraft>
};

type CustomFieldLike = {
  name: string;
  value: unknown;
};

function isCustomFieldLike(value: unknown): value is CustomFieldLike {

  return isObjectRecord(value) && typeof value.name === 'string' && 'value' in value;

}

function getCustomFields(object: CommerceToolsObject): Array<CustomFieldLike> {

  if (!object.custom) {

    return [];

  }

  if ('customFieldsRaw' in object.custom && Array.isArray(object.custom.customFieldsRaw)) {

    return object.custom.customFieldsRaw.filter(isCustomFieldLike);

  }

  if ('fields' in object.custom && Array.isArray(object.custom.fields)) {

    return object.custom.fields.filter(isCustomFieldLike);

  }

  return [];

}

export function filterLocaleMapValue(value: unknown, keepLocales: Set<string>): unknown {

  if (isObjectRecord(value)) {

    return Object.fromEntries(
      Object.entries(value).filter(([key]) => keepLocales.has(key)),
    );

  }

  return value;

}

export const findCustomFieldValue = (customFieldsRaw: RawCustomFields, fieldName: string) =>
  customFieldsRaw?.find(rawField => rawField?.name === fieldName)?.value;

export const isCustomFieldTruthy = (customFieldsRaw: RawCustomFields, fieldName: string): boolean => {

  const value = findCustomFieldValue(customFieldsRaw, fieldName);

  return value === true || value === StringBoolean.true;

};

export function extractCustomField(object: CommerceToolsObject, fieldName: string): string | undefined {

  const value = findCustomFieldValue(getCustomFields(object), fieldName);

  return typeof value === 'string' ? value : undefined;

}

export function extractLocalizedCustomField(
  object: CommerceToolsObject, fieldName: string, languageAndCountry: string,
): string | undefined {

  const value = findCustomFieldValue(getCustomFields(object), fieldName);

  if (!isObjectRecord(value)) {

    return undefined;

  }

  const localizedValue = Object.entries(value).find(
    ([key]) => key === languageAndCountry,
  )?.[1];

  return typeof localizedValue === 'string' ? localizedValue : undefined;

}
